import React, { useEffect, useState } from 'react';
import { getCharterConfig, updateCharterConfig } from './charterConfigApi';

// Peoria Charter configuration dashboard (Phase-1 skeleton). Reads the fully-resolved config
// (GET /api/charter/config) — the SAME object the Python planner reads each turn and the quote math
// resolves — and lets operations edit every knob (PUT). A generic renderer walks each domain
// (pricing, dot/feasibility folded into pricing, planner, validation, serviceArea) so new knobs
// appear automatically; Phase 6 will group/label/validate them properly.

// Nullable numeric fields (blank => null). Kind detection is value-based, so a null value would
// otherwise render as a text input — list them here so they stay number inputs when unset.
// shuttlePerHour: null = shuttle bills at the generic perHour until Peoria sets a rate.
const NUMERIC_NULLABLE = new Set(['maxRadiusMiles', 'shuttlePerHour']);

const DOMAIN_LABELS = {
  pricing: 'Pricing & DOT',
  planner: 'Planner behavior',
  validation: 'Validation thresholds',
  serviceArea: 'Service area',
};

function fieldKind(key, value) {
  if (typeof value === 'boolean') return 'boolean';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'number' || NUMERIC_NULLABLE.has(key)) return 'number';
  return 'string';
}

function coerce(kind, raw) {
  if (kind === 'boolean') return !!raw;
  if (kind === 'array') return String(raw).split(',').map((s) => s.trim()).filter(Boolean);
  if (kind === 'number') return raw === '' || raw == null ? null : Number(raw);
  return raw;
}

function inputValue(kind, value) {
  if (kind === 'array') return (value || []).join(', ');
  if (value == null) return '';
  return value;
}

export function CharterConfigPanel({ authSession }) {
  const [cfg, setCfg] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState('');

  useEffect(() => {
    let live = true;
    getCharterConfig(authSession)
      .then((c) => { if (live) setCfg(c); })
      .catch((e) => { if (live) setError(e.message || 'Failed to load config'); });
    return () => { live = false; };
  }, [authSession]);

  const setField = (domain, key, kind, raw) =>
    setCfg((prev) => ({ ...prev, [domain]: { ...prev[domain], [key]: coerce(kind, raw) } }));

  const save = async () => {
    setSaving(true); setError('');
    try {
      const saved = await updateCharterConfig(cfg, authSession);
      setCfg(saved);
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (error && !cfg) return <div className="min-h-screen bg-peoria-bg p-6 text-red-600 text-sm">Couldn't load config: {error}</div>;
  if (!cfg) return <div className="min-h-screen bg-peoria-bg p-6 text-peoria-muted text-sm">Loading configuration…</div>;

  const domains = Object.keys(cfg).filter((d) => cfg[d] && typeof cfg[d] === 'object');

  return (
    <div className="min-h-screen bg-peoria-bg text-peoria-text">
      <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold text-peoria-text">Peoria Charter — Configuration</h1>
        <div className="flex items-center gap-3">
          {savedAt && <span className="text-11 text-green-700">Saved {savedAt}</span>}
          {error && <span className="text-11 text-red-600">{error}</span>}
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-1.5 rounded-btn bg-peoria-red text-white hover:bg-peoria-red-dark text-13 font-medium disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
      <p className="text-11 text-peoria-muted mb-4 leading-relaxed">
        These values drive the planner and the quote/feasibility math directly — a change takes effect
        on the next quote and next conversation turn, with no code change or redeploy.
      </p>

      {domains.map((domain) => (
        <section key={domain} className="mb-6 border border-peoria-border bg-peoria-surface rounded-card p-4">
          <h2 className="text-13 font-semibold text-peoria-red uppercase tracking-brand mb-3">
            {DOMAIN_LABELS[domain] || domain}
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(cfg[domain]).map(([key, value]) => {
              const kind = fieldKind(key, value);
              return (
                <label key={key} className="flex flex-col gap-1 text-11">
                  <span className="text-peoria-muted">{key}</span>
                  {kind === 'boolean' ? (
                    <input
                      type="checkbox"
                      checked={!!value}
                      onChange={(e) => setField(domain, key, kind, e.target.checked)}
                      className="w-4 h-4 accent-peoria-red"
                    />
                  ) : (
                    <input
                      type={kind === 'number' ? 'number' : 'text'}
                      value={inputValue(kind, value)}
                      onChange={(e) => setField(domain, key, kind, e.target.value)}
                      className="border border-peoria-border bg-peoria-surface2 text-peoria-text rounded-btn px-2 py-1 text-13 font-mono focus:outline-none focus:ring-1 focus:ring-peoria-red/40 focus:border-peoria-red/60"
                    />
                  )}
                </label>
              );
            })}
          </div>
        </section>
      ))}
      </div>
    </div>
  );
}

export default CharterConfigPanel;
